<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';

import { getContratoDetail } from 'dashboard/api/supra';
import CeaLookupsAPI from 'dashboard/api/ceaLookups';

// Components
import ContratoInfoTab from './components/ContratoInfoTab.vue';
import ContratoConsumosTab from './components/ContratoConsumosTab.vue';
import ContratoTarifasTab from './components/ContratoTarifasTab.vue';
import ContratoFinancieroTab from './components/ContratoFinancieroTab.vue';
import ContratoLecturasTab from './components/ContratoLecturasTab.vue';
import ContratoFacturasTab from './components/ContratoFacturasTab.vue';
import ContratoTicketsTab from './components/ContratoTicketsTab.vue';
import ContratoOrdenesTab from './components/ContratoOrdenesTab.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// UI: Financiero tab enabled (see ContratoFinancieroTab).
const FINANCIERO_TAB_VISIBLE = true;

// State
const contrato = ref(null);
const isLoading = ref(true);
const error = ref(null);
const activeTab = ref('info');
const exploitationZones = ref([]);

// Computed
const contratoId = computed(() => route.params.contratoId);
const explotacion = ref(
  localStorage.getItem(`contrato_${contratoId.value}_explotacion`) || ''
);

const explotacionDesc = computed(() => {
  if (!contrato.value) return '';
  const code = String(contrato.value.explotacion);
  const zone = exploitationZones.value.find(z => String(z.code) === code);
  return zone ? `${zone.description} (${code})` : code;
});

// Methods
const fetchExploitationZones = async () => {
  try {
    const res = await CeaLookupsAPI.get('exploitation_zones');
    exploitationZones.value = Array.isArray(res.data) ? res.data : [];
  } catch {
    // silently ignore — fallback is the numeric code
  }
};
const fetchContrato = async () => {
  if (!contratoId.value) {
    error.value = 'No se especificó un número de contrato';
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    const res = await getContratoDetail(contratoId.value);
    const data = res.data?.data;

    if (!data || !data.contrato) {
      throw new Error('No se encontró información del contrato');
    }

    // Update explotacion from API, falling back to localStorage then '1'
    explotacion.value = String(data.explotacion || explotacion.value || '1');
    localStorage.setItem(
      `contrato_${contratoId.value}_explotacion`,
      explotacion.value
    );

    const direccion = data.calle
      ? `${data.calle} ${data.numero || ''}, ${data.colonia || ''}, ${data.municipio || ''}`.trim()
      : data.direccion || 'No especificada';

    contrato.value = {
      numeroContrato: data.contrato,
      explotacion: explotacion.value,
      titular: data.nombreTitular || 'Sin titular',
      direccion,
      tipoUso: data.tipoUsoCodigo || '',
      descUso: data.tipoUso || data.tarifa || 'No especificado',
      numeroContador: data.numeroMedidor || 'No disponible',
      fechaAlta: data.fechaAlta
        ? new Date(data.fechaAlta).toLocaleDateString('es-MX')
        : 'No disponible',
      fechaBaja: null,
      estado: data.estado || 'activo',
      rawData: data,
    };
  } catch (err) {
    error.value = err.message || 'Error desconocido al cargar el contrato';
    useAlert(error.value);
  } finally {
    isLoading.value = false;
  }
};

const tabs = computed(() => {
  const all = [
    { key: 'info', label: t('CONTRACTS.DETAIL.TAB_INFO'), icon: 'i-lucide-user' },
    {
      key: 'consumos',
      label: t('CONTRACTS.DETAIL.TAB_CONSUMOS'),
      icon: 'i-lucide-droplet',
    },
    {
      key: 'lecturas',
      label: t('CONTRACTS.DETAIL.TAB_LECTURAS'),
      icon: 'i-lucide-droplet',
    },
    {
      key: 'facturas',
      label: t('CONTRACTS.DETAIL.TAB_FACTURAS'),
      icon: 'i-lucide-file-text',
    },
    {
      key: 'tarifas',
      label: t('CONTRACTS.DETAIL.TAB_TARIFAS'),
      icon: 'i-lucide-dollar-sign',
    },
    {
      key: 'financiero',
      label: t('CONTRACTS.DETAIL.TAB_FINANCIERO'),
      icon: 'i-lucide-bar-chart-3',
    },
    {
      key: 'tickets',
      label: t('CONTRACTS.DETAIL.TAB_TICKETS'),
      icon: 'i-lucide-ticket',
    },
    {
      key: 'ordenes',
      label: t('CONTRACTS.DETAIL.TAB_ORDENES'),
      icon: 'i-lucide-clipboard-list',
    },
  ];
  if (FINANCIERO_TAB_VISIBLE) return all;
  return all.filter(tab => tab.key !== 'financiero');
});

onMounted(() => {
  fetchContrato();
  fetchExploitationZones();
});
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 bg-n-background">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <Spinner :size="32" />
        <h2 class="text-xl font-semibold mt-4">
          {{ $t('CONTRACTS.LOADING') }}
        </h2>
        <p class="text-sm text-n-slate-11 mt-2">
          {{ $t('CONTRACTS.DETAIL.LOADING_INFO', { id: contratoId }) }}
        </p>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error || !contrato"
      class="flex items-center justify-center py-12 px-4"
    >
      <div class="text-center max-w-xl">
        <span class="i-lucide-alert-circle text-6xl text-n-ruby-11 mb-4" />
        <h2 class="text-xl font-semibold px-8">
          {{ error || $t('CONTRACTS.DETAIL.NOT_FOUND') }}
        </h2>
        <p class="text-sm text-n-slate-11 mt-3 px-12">
          {{ error || $t('CONTRACTS.DETAIL.NOT_FOUND_DESC') }}
        </p>
        <div class="flex gap-2 justify-center mt-6">
          <Button color="blue" @click="router.back()">
            {{ $t('CONTRACTS.BACK') }}
          </Button>
          <Button color="slate" @click="fetchContrato">
            {{ $t('CONTRACTS.RETRY') }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Contract Detail -->
    <div v-else class="flex flex-col h-full overflow-hidden">
      <!-- Header -->
      <header class="flex items-center gap-4 p-4 border-b border-n-weak">
        <Button color="slate" size="small" @click="router.back()">
          <span class="i-lucide-arrow-left" />
        </Button>
        <div class="flex-1">
          <div class="flex items-center gap-3">
            <h1 class="text-2xl font-semibold text-n-slate-12">
              {{
                $t('CONTRACTS.DETAIL.CONTRACT_TITLE', {
                  number: contrato.numeroContrato,
                })
              }}
            </h1>
            <Badge
              :color="contrato.estado === 'activo' ? 'green' : 'gray'"
              size="small"
            >
              {{
                contrato.estado === 'activo'
                  ? $t('CONTRACTS.COMMON.ACTIVE')
                  : $t('CONTRACTS.COMMON.INACTIVE')
              }}
            </Badge>
          </div>
          <p class="text-sm text-n-slate-11 mt-1">
            {{
              $t('CONTRACTS.DETAIL.TITULAR_EXPLOITATION', {
                titular: contrato.titular,
                exploitation: explotacionDesc,
              })
            }}
          </p>
        </div>
      </header>

      <!-- Quick Info Cards -->
      <div
        class="grid grid-cols-4 gap-4 p-4 border-b border-n-weak bg-n-solid-3"
      >
        <div class="flex flex-col gap-1">
          <p class="text-xs text-n-slate-11">
            {{ $t('CONTRACTS.DETAIL.USE_TYPE') }}
          </p>
          <p class="text-sm font-semibold text-n-slate-12">
            {{ contrato.descUso }}
          </p>
          <p class="text-xs text-n-slate-10">
            {{ $t('CONTRACTS.DETAIL.CODE_PREFIX', { code: contrato.tipoUso }) }}
          </p>
        </div>
        <div class="flex flex-col gap-1">
          <p class="text-xs text-n-slate-11">
            {{ $t('CONTRACTS.DETAIL.METER') }}
          </p>
          <p class="text-sm font-semibold font-mono text-n-slate-12">
            {{ contrato.numeroContador }}
          </p>
          <p class="text-xs text-n-slate-10">
            {{ $t('CONTRACTS.DETAIL.SERIAL_NUMBER') }}
          </p>
        </div>
        <div class="flex flex-col gap-1">
          <p class="text-xs text-n-slate-11">
            {{ $t('CONTRACTS.DETAIL.START_DATE') }}
          </p>
          <p class="text-sm font-semibold text-n-slate-12">
            {{ contrato.fechaAlta }}
          </p>
          <p class="text-xs text-n-slate-10">
            {{
              contrato.fechaBaja
                ? $t('CONTRACTS.DETAIL.CANCELLATION_DATE', {
                    date: contrato.fechaBaja,
                  })
                : $t('CONTRACTS.DETAIL.ACTIVE_CONTRACT')
            }}
          </p>
        </div>
        <div class="flex flex-col gap-1">
          <p class="text-xs text-n-slate-11">
            {{ $t('CONTRACTS.DETAIL.LOCATION') }}
          </p>
          <p class="text-sm font-medium text-n-slate-12 line-clamp-2">
            {{ contrato.direccion }}
          </p>
        </div>
      </div>

      <!-- Tabs -->
      <div class="flex-1 overflow-hidden flex flex-col">
        <!-- Tab Navigation -->
        <div class="flex items-center border-b border-n-weak bg-n-solid-2 px-4">
          <nav class="flex gap-1">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              class="px-4 py-3 text-sm font-medium transition-colors relative border-b-2"
              :class="[
                activeTab === tab.key
                  ? 'text-n-brand border-n-brand'
                  : 'text-n-slate-11 hover:text-n-slate-12 border-transparent',
              ]"
              @click="activeTab = tab.key"
            >
              <span :class="tab.icon" class="mr-2" />
              {{ tab.label }}
            </button>
          </nav>
        </div>

        <!-- Tab Content -->
        <div class="flex-1 overflow-auto p-4">
          <ContratoInfoTab v-if="activeTab === 'info'" :contrato="contrato" :explotacion-desc="explotacionDesc" />
          <ContratoConsumosTab
            v-if="activeTab === 'consumos'"
            :numero-contrato="contrato.numeroContrato"
            :explotacion="contrato.explotacion"
          />
          <ContratoLecturasTab
            v-if="activeTab === 'lecturas'"
            :numero-contrato="contrato.numeroContrato"
            :explotacion="contrato.explotacion"
          />
          <ContratoFacturasTab
            v-if="activeTab === 'facturas'"
            :numero-contrato="contrato.numeroContrato"
            :explotacion="contrato.explotacion"
          />
          <ContratoTarifasTab
            v-if="activeTab === 'tarifas'"
            :numero-contrato="contrato.numeroContrato"
            :explotacion="contrato.explotacion"
          />
          <ContratoFinancieroTab
            v-if="activeTab === 'financiero'"
            :numero-contrato="contrato.numeroContrato"
            :explotacion="contrato.explotacion"
            :contrato="contrato"
          />
          <ContratoTicketsTab
            v-if="activeTab === 'tickets'"
            :numero-contrato="contrato.numeroContrato"
          />
          <ContratoOrdenesTab
            v-if="activeTab === 'ordenes'"
            :numero-contrato="contrato.numeroContrato"
          />
        </div>
      </div>
    </div>
  </div>
</template>
